import React, { useState, useEffect, useRef, useImperativeHandle } from "react"
import { Row, Col, message, Button, useCRUDController, Modal, Card, getValueByCode, responsesToKeyValueTableData } from './ot'
import BaseDocumentResponseCanvasEdit from './base_document_response_canvas_edit'
import FormStatic5, { getValByCode, ReadOnlyDocumentItem } from './form_static_5'
import { toPng } from 'html-to-image';
import { Loader2 } from 'lucide-react';

function getExtraByCode(extra_data = [], code) {
    const item = extra_data.find(e =>
        (e.form_column_form_value_data || e.form_column_form_value_data2 || []).find(c => c.code === code)
    )
    return item?.value?.value != null ? String(item.value.value) : ''
}

// ─── 收據排版元件（隱藏，只用來截圖）────────────────────────────────────────
const ReceiptContent = React.forwardRef(({ headerResponseValue, bodyResponseValues = [], signatureImageUrl = null }, ref) => {
    const hf = headerResponseValue?.response_form_value_data || []

    // 單頭資料
    const orderName = getValByCode(hf, '名稱') || '銷貨訂單'
    const responseId = headerResponseValue?.response_id || ''
    const delivDate = getValByCode(hf, '預計交付日期')
    const orderDate = getValByCode(hf, '訂單建立日期')

    const customerItem = hf.find(i => (i.form_column_form_value_data || []).find(c => c.code === '客戶名稱'))
    const customerName = customerItem ? getExtraByCode(customerItem.extra_data, '名稱') : ''
    const custNo = customerItem ? getExtraByCode(customerItem.extra_data, '客戶編號') : ''
    const contact = customerItem ? getExtraByCode(customerItem.extra_data, '聯絡人') : getValByCode(hf, '聯絡人')
    const phone = customerItem ? getExtraByCode(customerItem.extra_data, '聯絡電話') : getValByCode(hf, '聯絡電話')
    const addr = customerItem ? getExtraByCode(customerItem.extra_data, '地址') : ''
    const totalAmt = getValByCode(hf, '總金額')
    const paidAmt = getValByCode(hf, '已收金額')
    const unpaidAmt = getValByCode(hf, '未收金額')
    const desc = getValByCode(hf, '描述')

    // 單身資料
    const bodyItems = bodyResponseValues.map(rv => {
        const bf = rv?.response_form_value_data || []
        const productItem = bf.find(i => (i.form_column_form_value_data || []).find(c => c.code === '商品名稱'))
        const productName = productItem
            ? (getExtraByCode(productItem.extra_data, '名稱') || getValByCode(bf, '商品名稱'))
            : getValByCode(bf, '商品名稱')
        const productNo = productItem ? getExtraByCode(productItem.extra_data, '商品編號') : ''
        const qty = getValByCode(bf, '販售數量')
        const unit = getValByCode(bf, '販售單位') || (productItem ? getExtraByCode(productItem.extra_data, '販售單位') : '')
        const unitPrice = getValByCode(bf, '單價')
        const subtotal = getValByCode(bf, '小計')
        return { productName, productNo, qty, unit, unitPrice, subtotal }
    })

    const grandTotal = totalAmt || bodyItems.reduce((s, i) => s + (parseFloat(i.subtotal) || 0), 0).toString()

    const S = {
        wrap: {
            background: '#ffffff',
            width: '280px',
            padding: '12px 10px',
            fontFamily: "'Courier New', Courier, monospace",
            fontSize: '13px',
            color: '#111',
            lineHeight: '1.5',
        },
        center: { textAlign: 'center' },
        title: { fontSize: '16px', fontWeight: '700', textAlign: 'center', marginBottom: '2px' },
        sub: { fontSize: '11px', textAlign: 'center', marginBottom: '1px' },
        dashed: { borderTop: '1px dashed #888', margin: '6px 0' },
        solid: { borderTop: '1px solid #333', margin: '6px 0' },
        row: { display: 'flex', justifyContent: 'space-between', marginBottom: '2px' },
        colHeader: { display: 'flex', fontWeight: '700', fontSize: '12px', borderBottom: '1px solid #333', paddingBottom: '3px', marginBottom: '3px' },
        itemRow: { display: 'flex', marginBottom: '4px', alignItems: 'flex-start' },
        colName: { flex: 1 },
        colQty: { width: '40px', textAlign: 'center' },
        colPrice: { width: '52px', textAlign: 'right' },
        colAmt: { width: '52px', textAlign: 'right' },
        totalRow: { display: 'flex', justifyContent: 'space-between', fontWeight: '700', marginBottom: '2px' },
        grandRow: { display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '15px', marginBottom: '2px' },
        foot: { fontSize: '11px', textAlign: 'center', color: '#555', marginTop: '6px' },
    }

    return (
        <div ref={ref} style={S.wrap}>
            <div style={S.title}>{orderName}</div>
            {/* {responseId && <div style={S.sub}>單號：{responseId}</div>} */}
            {orderDate && <div style={S.sub}>建立時間：{orderDate}</div>}
            {delivDate && <div style={S.sub}>交付時間：{delivDate}</div>}
            {customerName && <div style={S.sub}>客戶：{customerName}{custNo ? `（${custNo}）` : ''}</div>}

            <div style={S.dashed} />

            {contact && <div style={S.row}><span>聯絡人</span><span>{contact}</span></div>}
            {phone && <div style={S.row}><span>電話</span><span>{phone}</span></div>}
            {addr && <div style={S.row}><span>地址</span><span style={{ textAlign: 'right', maxWidth: '160px' }}>{addr}</span></div>}

            <div style={S.dashed} />

            <div style={S.colHeader}>
                <span style={S.colName}>品名</span>
                <span style={S.colQty}>數量</span>
                <span style={S.colPrice}>單價</span>
                <span style={S.colAmt}>金額</span>
            </div>

            {bodyItems.map((item, idx) => (
                <div key={idx} style={S.itemRow}>
                    <span style={S.colName}>
                        {item.productName}
                        {item.productNo ? <span style={{ fontSize: '10px', color: '#555', display: 'block' }}>{item.productNo}</span> : null}
                    </span>
                    <span style={S.colQty}>{item.qty}{item.unit}</span>
                    <span style={S.colPrice}>{item.unitPrice}</span>
                    <span style={S.colAmt}>{item.subtotal}</span>
                </div>
            ))}

            {/* <div style={S.totalRow}><span>小計</span><span>{bodyItems.reduce((s, i) => s + (parseFloat(i.subtotal) || 0), 0)}</span></div> */}
            <div style={S.grandRow}><span>總金額</span><span>{grandTotal}元</span></div>
            {/* {paidAmt && <div style={S.row}><span>已收</span><span>{paidAmt}元</span></div>} */}
            {/* {unpaidAmt && <div style={S.row}><span>未收</span><span>{unpaidAmt}元</span></div>} */}

            <div style={S.dashed} />
            <div style={S.foot}>{desc ? `備註：${desc}` : '感謝您的訂購'}</div>
            {signatureImageUrl && (
                <>
                    <div style={S.dashed} />
                    <div style={{ fontSize: '11px', color: '#555', marginBottom: '4px', textAlign: 'center' }}>客戶簽收</div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <img
                            src={signatureImageUrl}
                            alt="簽收圖片"
                            style={{ maxWidth: '240px', maxHeight: '120px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px' }}
                            crossOrigin="anonymous"
                        />
                    </div>
                </>
            )}
        </div>
    )
})

// ─── CPCL 列印預覽 Modal ──────────────────────────────────────────────────────
const PrintPreviewModal = ({ previewUrl, onClose, onConfirm, loading, deviceName }) => {
    if (!previewUrl) return null
    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.55)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
            <div style={{
                background: '#fff', borderRadius: 8, width: 400, maxWidth: '95vw',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                position: 'relative',
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
                }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>列印預覽</span>
                    <button onClick={onClose} disabled={loading}
                        style={{ background: 'none', border: 'none', fontSize: 20, cursor: loading ? 'not-allowed' : 'pointer', color: loading ? '#ccc' : '#888', lineHeight: 1 }}>×</button>
                </div>
                <div style={{ padding: 16, background: '#f5f5f5', display: 'flex', justifyContent: 'center', maxHeight: '60vh', overflowY: 'auto' }}>
                    <img src={previewUrl} alt="列印預覽" style={{ maxWidth: '100%', border: '1px solid #ddd', background: '#fff' }} />
                </div>
                <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid #f0f0f0' }}>
                    <Button style={{ flex: 1, height: 40 }} onClick={onClose} disabled={loading}>取消</Button>
                    <Button type="primary" loading={loading} style={{ flex: 1, height: 40 }} onClick={onConfirm}>
                        {loading ? '傳送中...' : `🖨️ CPCL 列印「${deviceName ?? ''}」`}
                    </Button>
                </div>

                {/* 列印中全版遮罩 */}
                {loading && (
                    <div style={{
                        position: 'absolute', inset: 0,
                        background: 'rgba(255,255,255,0.75)',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        gap: 12, zIndex: 1,
                    }}>
                        <Loader2 className="h-10 w-10 animate-spin text-primary" />
                        <span style={{ fontSize: 14, color: '#1677ff', fontWeight: 600 }}>傳送列印指令中...</span>
                    </div>
                )}
            </div>
        </div>
    )
}

const Render = ({ children, axiosMode, setOpen, loading, handleOk, okText, printPending }) => (
    <div>
        {children}
        {axiosMode !== "get" && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <Button
                    onClick={() => setOpen(false)}
                    disabled={printPending}
                    style={{ flex: '0 0 30%', height: '44px', backgroundColor: '#f5f5f5', color: '#666', border: '1px solid #e8e8e8', borderRadius: '8px' }}
                >
                    關閉
                </Button>
                <Button
                    type="primary"
                    loading={loading}
                    onClick={handleOk}
                    style={{ flex: '0 0 68%', height: '44px', fontSize: '15px', fontWeight: '600', borderRadius: '8px', backgroundColor: '#fa8c16', border: 'none' }}
                >
                    {okText}
                </Button>
            </div>
        )}
    </div>
)

const BaseDocumentResponseCanvasEditModal = React.forwardRef(({
    formComponent = "BaseDocumentResponseCanvasEdit",
    setOpen = () => { },
    open = false,
    title = "編輯",
    width = 2000,
    record,
    axiosMode = "post",
    documentApiRoute = '/access_control_management/backend/access_control_document/overview',
    onFinish = ({ axiosMode, status, values }) => { },
    processResponseData,
    hasBody = false,
    bodyDocumentApiRoute = '/ad_plan_management/backend/form_static_document/base_view',
    bodyHeight = {
        head: '100vh',
        body: '70vh',
        extraBody: "20vh",
    },
    bodyRecord = [],
    bodyCreateTitle = "新增項目",
    bodyDeleteTitle = "刪除項目",
    responseWithoutViewApiRoute = "/utility/response/cache",
    bodyHeadCode = "綁定單頭",
    viewMode = "modal",
    okText = "確定",
    propsLoadRecord = false,
    propsRecordCalculate = [],
    showDocumentButton = true,
    headerTitle = "單頭標題",
    bodyTitle = "單身標題",
    reponseApiRoute,
    defaultRecord,
    propsPrint = false,
    hiddenBody = false,
    hiddenCreateButton = false,
    preloadedHeaderDocument = null,  // null = 還沒好，走原本 API
    preloadedBodyDocument = null,
    preloadedHeaderCanvasDocument = null,
    preloadedBodyCanvasDocument = null,
    propsCanvasMode,
    propsBodyCanvasMode,
    propsBodyAutoFitWidth,
    printAfterSubmit = false,     // 送出成功後自動觸發列印預覽，列印完成（或關閉預覽）再呼叫 onFinish
    bodyReadOnly = false,         // 單身區域僅呈現唯讀，使用 ReadOnlyDocumentItem 模式
    bodyColumnLinks = {},         // { [code]: string | (value) => string } — 傳給單身 ReadOnlyDocumentItem
    bodyColumnOnClick = {},       // { [code]: (value) => void } — 傳給單身 ReadOnlyDocumentItem
    headerReadOnly = false,       // 單頭區域僅呈現唯讀，使用 ReadOnlyDocumentItem 模式
    headerColumnLinks = {},       // { [code]: string | (value) => string } — 傳給單頭 ReadOnlyDocumentItem
    headerColumnOnClick = {},     // { [code]: (value) => void } — 傳給單頭 ReadOnlyDocumentItem
    headerVisibleFields = [],     // [{ code: '後台代碼', label: '顯示別名' }]，控制單頭顯示欄位及標籤
    bodyVisibleFields = [],       // [{ code: '後台代碼', label: '顯示別名' }]，控制單身每列顯示欄位及標籤
    // ── 第二 visible body（例：支票明細）──────────────────────────────────────
    hasExtraBody = false,
    extraBodyDocumentApiRoute = '',
    extraBodyTitle = "支票明細",
    extraBodyCreateTitle = "新增支票",
    extraBodyDeleteTitle = "刪除支票",
    extraBodyHeadCode = "綁定單頭",
    extraBodyRecord = [],
    extraBodyRecordCalculate = [],  // [{ major: "支票小計", minor: "票額", calculation: "加" }]
    headerPartialEdit = false,       // true = 部分唯讀，只有 headerEditableFieldCodes 列出的欄位可編輯
    headerEditableFieldCodes = null, // ['現金', '全額收現', '此筆未收']
}, outerRef) => {
    const submitRef = useRef(null)
    const submitBodyRef = useRef([])
    const submitExtraBodyRef = useRef([])
    const bodyHeadResponseRef = useRef([])
    const deleteBodyDataRef = useRef([])
    const deleteExtraBodyDataRef = useRef([])
    const headerResponseRef = useRef(null)
    const headerPayloadRef = useRef(null)
    const pendingFinishArgsRef = useRef(null)
    const sectionsRef = useRef([])

    // 若 printAfterSubmit=true，送出成功後先列印再呼叫 onFinish
    // printPending=true 期間封鎖 Modal 關閉，避免列印預覽期間使用者誤關第一個 Modal
    const [printPending, setPrintPending] = useState(false)

    const flushPendingFinish = () => {
        // 清除簽收圖片 state 和 header override
        setSignatureImageUrl(null)
        setReceiptHeaderOverride(null)
        // 若有外部列印完成 callback（由 triggerPrint 傳入），呼叫它
        if (externalPrintCallbackRef.current) {
            const cb = externalPrintCallbackRef.current
            externalPrintCallbackRef.current = null
            cb()
            return
        }
        if (pendingFinishArgsRef.current) {
            const args = pendingFinishArgsRef.current
            pendingFinishArgsRef.current = null
            setPrintPending(false)
            onFinish({ ...args, bodyValues: bodySnapshotRef.current })
        }
    }
    // 商品行快照 ref：送出成功時存入，供外部列印時使用（此時 DOM 還在）
    const bodySnapshotRef = useRef([])

    const emitOnFinish = (args) => {
        // 送出成功時，趁 DOM 還在，快照商品行資料
        if (args.status === 'success') {
            bodySnapshotRef.current = getBodyResponseValuesFromRefs()
        }
        if (printAfterSubmit && args.status === 'success') {
            pendingFinishArgsRef.current = args
            setPrintPending(true)
            handlePrint()
            return
        }
        onFinish({ ...args, bodyValues: bodySnapshotRef.current })
    }

    const headerFetchController = useCRUDController({
        apiRoute: responseWithoutViewApiRoute,
    })

    const handleOk = () => {
        if (headerReadOnly) {
            onFinish({ axiosMode, status: 'success', values: record?.responseValue, headerValues: record?.responseValue })
            return
        }
        const submitResult = submitRef.current?.submit()
        if (submitResult) {
            setLoading(true)
        }
    }
    const [postSetting, setPostSetting] = useState({})
    const [postBodySetting, setPostBodySetting] = useState({})
    const [extraPostBodySetting, setExtraPostBodySetting] = useState({})
    const [data, setData] = useState(record ?? [])
    const [dataLoading, setDataLoading] = useState(false)
    const [bodyData, setBodyData] = useState(bodyRecord ?? [])
    const [extraBodyData, setExtraBodyData] = useState(extraBodyRecord ?? [])
    const [loading, setLoading] = useState(false)
    const [bodyDocumentColumns, setBodyDocumentColumns] = useState([])
    const [headerDocumentColumns, setHeaderDocumentColumns] = useState([])
    const labelRef = useRef(null);
    const [status, setStatus] = useState("");
    const [previewUrl, setPreviewUrl] = useState(null);
    const [liveBodyResponseValues, setLiveBodyResponseValues] = useState([])

    // ─── 新增：收據 ref + CPCL 預覽 state ────────────────────────────────────
    const receiptRef = useRef(null)
    const [cpclPreviewUrl, setCpclPreviewUrl] = useState(null)
    const [cpclPrintLoading, setCpclPrintLoading] = useState(false)
    const cpclImageDataRef = useRef(null)

    // ─── 簽收圖片（由外部透過 triggerPrint 傳入）─────────────────────────────
    const [signatureImageUrl, setSignatureImageUrl] = useState(null)
    // 外部列印時覆蓋 ReceiptContent 的 header 資料（主 Modal open=false 後 record 會被清空）
    const [receiptHeaderOverride, setReceiptHeaderOverride] = useState(null)
    // 列印完成後要呼叫的 callback（由外部透過 triggerPrint 傳入）
    const externalPrintCallbackRef = useRef(null)

    // ─── 暴露 triggerPrint 給外部 ref 呼叫──────────────────────────────────
    // 用法：modalRef.current.triggerPrint({ signatureImageUrl, headerResponseValue, bodyResponseValues, onDone })
    // onDone 會在列印確認或取消後被呼叫
    useImperativeHandle(outerRef, () => ({
        triggerPrint: ({
            signatureImageUrl: sigUrl = null,
            headerResponseValue: extHeaderValue = null,
            bodyResponseValues: extBodyValues = null,
            onDone = () => { }
        } = {}) => {
            setSignatureImageUrl(sigUrl)
            // 若外部有傳入資料，覆蓋 liveBodyResponseValues 和 receiptHeaderOverride
            if (extHeaderValue !== null) setReceiptHeaderOverride(extHeaderValue)
            if (extBodyValues !== null) setLiveBodyResponseValues(extBodyValues)
            externalPrintCallbackRef.current = onDone
            // 等 React 把資料渲染進 ReceiptContent 後再截圖
            setTimeout(() => {
                handlePrint({ _externalTrigger: true })
            }, 150)
        }
    }))

    const getDocumentData = () => {
        if (documentApiRoute !== "/utility/document/base_on_classify_structure") {
            if (record?.document_id) {
                setPostSetting({
                    dbCoverId: record?.document_id
                })
                return
            }
            // preload 有資料才用，null 就走原本邏輯
            if (preloadedHeaderDocument) {
                let dataFirst = preloadedHeaderDocument.find(() => true)
                setPostSetting({
                    dbCoverId: record?.document_id ?? dataFirst?.document_id,
                })
                if (propsLoadRecord && record?.responseValue?.response_id) {
                    setDataLoading(true)
                    const res = responseUtilityController.getData({
                        document_id: record?.document_id ? record?.document_id : dataFirst?.document_id,
                        response_id: record?.responseValue?.response_id
                    })
                    res.then((response) => {
                        setData(response?.data?.map(item => ({
                            responseValue: item,
                        })))
                        setDataLoading(false)
                    })
                }
                return
            }
            documentController.getData({})
                .then((response) => {
                    let data = response?.data
                    let dataFirst = data.find((_) => true)
                    setPostSetting({
                        dbCoverId: record?.document_id ? record?.document_id : dataFirst?.document_id,
                    })
                    if (propsLoadRecord && record?.responseValue?.response_id) {
                        setDataLoading(true)
                        const res = responseUtilityController.getData({
                            document_id: record?.document_id ? record?.document_id : dataFirst?.document_id,
                            response_id: record?.responseValue?.response_id
                        })
                        res.then((response) => {
                            setData(response?.data?.map(item => ({
                                responseValue: item,
                            })))
                            setDataLoading(false)
                        })
                    }
                })
        }

    }
    const documentController = useCRUDController({
        apiRoute: documentApiRoute,
        onBefore: ({ axiosMode }) => {
            if (axiosMode !== "get") {
                setLoading(true);
            }
        },
        onFinish: ({ axiosMode, status, values }) => {
            if (status === "success") {
                if (axiosMode === "post") {
                    message.success("新增成功")
                } else if (axiosMode === "patch") {
                    message.success("修改成功")
                }
                else if (axiosMode === "delete") {
                    message.success("刪除成功")
                }
            }
            else if (status === "error") {
                if (axiosMode === "post") {
                    message.error("新增失敗")
                }
                else if (axiosMode === "patch") {
                    message.error("修改失敗")
                }
                else if (axiosMode === "delete") {
                    message.error("刪除失敗")
                }
            }
        }
    })

    const getBodyDocumentData = ({ skipSetBodyData = false } = {}) => {
        if (bodyDocumentApiRoute !== "/utility/document/base_on_classify_structure") {
            if (preloadedBodyDocument) {
                // preload 有資料時直接用，不管 post/patch 都能取到正確的 dbCoverId
                let dataFirst = preloadedBodyDocument.find(() => true)
                const dbCoverId = dataFirst?.document_id
                setPostBodySetting({ dbCoverId })
                // skipSetBodyData=true（hiddenBody 模式）只取 dbCoverId，不查歷史商品
                if (skipSetBodyData) return
                // 有關聯 response_id 時（新建帶歷史 or 已開單編輯），撈對應商品帶入 bodyData
                if (record?.responseValue?.response_id) {
                    responseWithoutViewUtilityController.getData({
                        document_id: dbCoverId,
                        filter: [
                            {
                                include: "IN",
                                code: '綁定單頭',
                                code_major: '綁定單頭',
                                filter_document_data: [{ document_id: dbCoverId }],
                                condition: "等於",
                                code_minor: null,
                                value_type: "固定值",
                                value: { value: record?.responseValue?.response_id },
                            }
                        ]
                    }).then((response) => {
                        const items = response?.data?.map(item => ({ responseValue: item })) || []
                        if (items.length > 0) setBodyData(items)
                    }).catch(() => { })
                }
                return
            }
            bodyDocumentController.getData({})
                .then((response) => {
                    let data = response?.data
                    let dataFirst = data.find((_) => true)
                    setPostBodySetting({
                        dbCoverId: bodyRecord?.document_id ? bodyRecord?.document_id : dataFirst?.document_id,
                    })
                    // skipSetBodyData=true 時（hiddenBody 模式）只取 dbCoverId，不覆蓋預填的 bodyData
                    if (skipSetBodyData) return
                    responseWithoutViewUtilityController.getData({
                        document_id: bodyRecord?.document_id ? bodyRecord?.document_id : dataFirst?.document_id,
                        filter: [
                            {
                                include: "IN",
                                code: '綁定單頭',
                                code_major: '綁定單頭',
                                filter_document_data: [
                                    {
                                        document_id: bodyRecord?.document_id ? bodyRecord?.document_id : dataFirst?.document_id,
                                    }
                                ],
                                condition: "等於",
                                code_minor: null,
                                value_type: "固定值",
                                value: {
                                    value: record?.responseValue?.response_id,
                                }
                            }
                        ]
                    })
                        .then((response) => {
                            setBodyData(response?.data?.map(item => ({
                                responseValue: item,
                            })))
                        })
                        .catch(() => {

                        })
                })
        }

    }
    const bodyDocumentController = useCRUDController({
        apiRoute: bodyDocumentApiRoute,
        onBefore: ({ axiosMode }) => {
            if (axiosMode !== "get") {
                setLoading(true);
            }
        },
        onFinish: ({ axiosMode, status, values }) => {
            if (status === "success") {
                if (axiosMode === "post") {
                    message.success("新增成功")
                } else if (axiosMode === "patch") {
                    message.success("修改成功")
                }
                else if (axiosMode === "delete") {
                    message.success("刪除成功")
                }
            }
            else if (status === "error") {
                if (axiosMode === "post") {
                    message.error("新增失敗")
                }
                else if (axiosMode === "patch") {
                    message.error("修改失敗")
                }
                else if (axiosMode === "delete") {
                    message.error("刪除失敗")
                }
            }
        }
    })
    const responseWithoutViewUtilityController = useCRUDController({
        apiRoute: responseWithoutViewApiRoute,
        onBefore: ({ axiosMode }) => { if (axiosMode !== "get") setLoading(true) },
        onFinish: () => { }
    })
    const responseUtilityController = useCRUDController({
        apiRoute: responseWithoutViewApiRoute,
        onBefore: ({ axiosMode }) => { if (axiosMode !== "get") setLoading(true) },
        onFinish: () => { }
    })

    // ── extra body document 載入 ──────────────────────────────────────────
    const extraBodyDocumentController = useCRUDController({
        apiRoute: extraBodyDocumentApiRoute || documentApiRoute,
        onBefore: ({ axiosMode }) => { if (axiosMode !== "get") setLoading(true) },
        onFinish: () => { },
    })
    const getExtraBodyDocumentData = () => {
        if (!hasExtraBody || !extraBodyDocumentApiRoute) return
        extraBodyDocumentController.getData({}).then((response) => {
            const dataFirst = response?.data?.find(_ => true)
            setExtraPostBodySetting({ dbCoverId: dataFirst?.document_id })
        })
    }

    // ── 統一 body section 工具函式 ────────────────────────────────────────
    // 將指定 headCode 欄位替換為單頭 response_id
    const makeProcessSectionData = (headCode) => (items_) => {
        const items = Array.isArray(items_) ? items_ : [items_]
        const result = items.map((item) => {
            if (!Array.isArray(item?.response_form_value_data)) return item
            return {
                ...item,
                response_form_value_data: item.response_form_value_data.map((col) => {
                    if (col.form_column_form_value_data?.[0]?.code === headCode) {
                        // patch 時 body 本身已有正確的綁定單頭，不覆蓋
                        if (axiosMode === "patch" && col.value?.value) return { ...col }
                        // post 時注入單頭送出後拿到的 response_id
                        return { ...col, value: { value: bodyHeadResponseRef.current?.response_id ?? record?.responseValue?.response_id } }
                    }
                    return { ...col }
                })
            }
        })
        return Array.isArray(items_) ? result : result?.find(_ => true)
    }

    // 刪除已移除的 items 並提交該 section 所有 refs
    const submitSection = (sectionIdx) => {
        const section = sectionsRef.current[sectionIdx]
        if (!section) return
        if (section.deleteRef.current?.length > 0) {
            responseWithoutViewUtilityController.deleteData(section.deleteRef.current.map(i => i?.responseValue))
            section.deleteRef.current = []
        }
        section.submitRef.current.forEach(ref => { if (ref?.submit()) setLoading(true) })
    }

    // 某 section 最後一筆 item 完成後呼叫，決定繼續下一個 section 或 emitOnFinish
    const handleSectionItemFinish = (sectionIdx, itemIdx, finishArgs) => {
        const sections = sectionsRef.current
        const section = sections[sectionIdx]
        if (!section || section.data.length - 1 !== itemIdx) return
        for (let i = sectionIdx + 1; i < sections.length; i++) {
            if (sections[i].submitRef.current.length > 0) {
                submitSection(i)
                return
            }
        }
        setLoading(false)
        emitOnFinish({ ...finishArgs, headerValues: headerResponseRef.current ?? record?.responseValue, payload: headerPayloadRef.current })
    }

    const collectRefData = (refArray) => {
        const result = []
        refArray.forEach((ref) => {
            const submitResult = ref?.getCurrentResponseData()
            if (Array.isArray(submitResult)) submitResult.forEach(item => result.push(item))
            else if (submitResult) result.push(submitResult)
        })
        return result
    }

    const applyCalculate = (calcList, sourceData, items_) => {
        let finalItems = items_
        calcList.forEach(({ major, minor, calculation }) => {
            let total = 0
            sourceData.forEach((item) => {
                if (Array.isArray(item?.response_form_value_data)) {
                    const value = parseFloat(getValueByCode({ code: minor, response_form_value_data: item.response_form_value_data })) || 0
                    if (calculation === "加") total += value
                    else if (calculation === "減") total -= value
                }
            })
            const sourceItems = Array.isArray(finalItems) ? finalItems : [finalItems]
            finalItems = sourceItems.map((item) => {
                if (!Array.isArray(item?.response_form_value_data)) return item
                return {
                    ...item,
                    response_form_value_data: item.response_form_value_data.map((col) => {
                        if (col.form_column_form_value_data?.[0]?.code === major) {
                            return { ...col, value: { value: total.toString() } }
                        }
                        return { ...col }
                    })
                }
            })
            finalItems = Array.isArray(items_) ? finalItems : finalItems?.[0]
        })
        return finalItems
    }

    const processHeaderResponseData = (items_) => {
        let finalItems = items_
        if (propsRecordCalculate.length > 0) {
            finalItems = applyCalculate(propsRecordCalculate, collectRefData(submitBodyRef.current), finalItems)
        }
        if (extraBodyRecordCalculate.length > 0) {
            finalItems = applyCalculate(extraBodyRecordCalculate, collectRefData(submitExtraBodyRef.current), finalItems)
        }
        if (processResponseData) finalItems = processResponseData(finalItems)
        return finalItems
    }

    function imageToCPCL(imageData, x = 0, y = 0) {
        const { width, height, data } = imageData;
        const bytesPerRow = Math.ceil(width / 8);
        let hexData = "";

        for (let row = 0; row < height; row++) {
            for (let byteIdx = 0; byteIdx < bytesPerRow; byteIdx++) {
                let byte = 0;
                for (let bit = 0; bit < 8; bit++) {
                    const pixelX = byteIdx * 8 + bit;
                    if (pixelX < width) {
                        const idx = (row * width + pixelX) * 4;
                        const r = data[idx];
                        const g = data[idx + 1];
                        const b = data[idx + 2];
                        const gray = 0.299 * r + 0.587 * g + 0.114 * b;
                        if (gray < 128) byte |= 1 << (7 - bit);
                    }
                }
                hexData += byte.toString(16).padStart(2, "0").toUpperCase();
            }
        }

        return (
            `! 0 200 200 ${height} 1\r\n` +
            `EG ${bytesPerRow} ${height} ${x} ${y} ${hexData}\r\n` +
            `PRINT\r\n`
        );
    }
    const handlePrint = async ({ _externalTrigger = false } = {}) => {
        // 外部觸發（簽收完成後）不需要 labelRef，只需要 receiptRef
        if (!_externalTrigger && !labelRef.current) return;
        try {
            setStatus("📸 截圖中...");

            const selectedDevice = window.selected_device;

            if (selectedDevice) {
                // if (true) {
                // ── CPCL 路徑：截收據排版（receiptRef），而不是截表單 ─────────
                if (!receiptRef.current) {
                    setStatus("❌ 找不到收據內容");
                    return;
                }


                // ★ 取即時資料後先更新 state，等 React 重繪完再截圖
                const liveBodyValues = _externalTrigger ? liveBodyResponseValues : getBodyResponseValuesFromRefs()
                if (!_externalTrigger) setLiveBodyResponseValues(liveBodyValues)

                // 等一個 tick 確保 React 已重繪（signatureImageUrl / liveBodyValues 都已更新）
                await new Promise(r => setTimeout(r, 0))

                const dataUrl = await toPng(receiptRef.current, {
                    backgroundColor: "#ffffff",
                    pixelRatio: 2,
                });

                // 備好 imageData 供確認後送印
                const img = new Image();
                img.src = dataUrl;
                await new Promise((resolve) => (img.onload = resolve));

                const MAX_WIDTH = 600;
                const scale = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1;
                const printW = Math.floor(img.width * scale);
                const printH = Math.floor(img.height * scale);

                const canvas = document.createElement("canvas");
                canvas.width = printW;
                canvas.height = printH;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, printW, printH);
                cpclImageDataRef.current = ctx.getImageData(0, 0, printW, printH);

                setCpclPreviewUrl(dataUrl);
                setStatus("");

            } else {
                // ── 瀏覽器預覽列印（原本邏輯完全不動）───────────────────────
                setStatus("🖨️ 開啟預覽列印...");

                const dataUrl = await toPng(labelRef.current, {
                    backgroundColor: "#ffffff",
                    pixelRatio: 1,
                });
                const img = new Image();
                img.src = dataUrl;
                await new Promise((resolve) => (img.onload = resolve));

                browserPrint(
                    dataUrl,
                    img.width,
                    img.height
                );
                setStatus("✅ 已開啟預覽列印視窗");
                flushPendingFinish();
            }

        } catch (err) {
            console.error(err);
            setStatus(`❌ 錯誤：${err.message}`);
        }
    };

    // ─── 使用者在預覽 Modal 確認後執行 CPCL 送印 ─────────────────────────────
    const handleCpclConfirmPrint = async () => {
        const selectedDevice = window.selected_device;
        if (!cpclImageDataRef.current || !selectedDevice) return;
        setCpclPrintLoading(true);
        await new Promise(r => setTimeout(r, 0)); // yield to let React flush the loading state
        try {
            setStatus("🔄 轉換 CPCL 指令...");
            const cpcl = imageToCPCL(cpclImageDataRef.current, 0, 0);
            setStatus(`🖨️ 傳送至裝置「${selectedDevice.name || "印表機"}」...`);
            await window.writeToSelectedPrinter(cpcl);
            setStatus("✅ CPCL 列印成功！");
        } catch (err) {
            console.error(err);
            setStatus(`❌ 錯誤：${err.message}`);
        } finally {
            setCpclPrintLoading(false);
            setCpclPreviewUrl(null);
            cpclImageDataRef.current = null;
            flushPendingFinish();
        }
    };

    function browserPrint(dataUrl, width, height) {
        const oldFrame = document.getElementById("print-frame");
        if (oldFrame) oldFrame.remove();

        const iframe = document.createElement("iframe");
        iframe.id = "print-frame";
        iframe.style.cssText = "position:fixed;top:0;left:0;width:0;height:0;border:0;visibility:hidden;";
        document.body.appendChild(iframe);

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <style>
                    * { margin: 0; padding: 0; }
                    @media print {
                        @page { margin: 0; }
                        body { margin: 10mm; }
                    }
                </style>
            </head>
            <body>
                <img src="${dataUrl}" width="${width}" height="${height}" />
            </body>
            </html>
        `;

        iframe.srcdoc = html;

        iframe.onload = () => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => iframe.remove(), 1000);
        };
    }

    const getBodyResponseValuesFromRefs = () => {
        const result = []
        submitBodyRef.current.forEach((ref) => {
            if (!ref) return
            const data = ref.getCurrentResponseData()
            // getCurrentResponseData 回傳的是 post 格式 array 或 patch 格式 object
            // 統一取出 response_form_value_data 組成 ReceiptContent 需要的結構
            const items = Array.isArray(data) ? data : [data]
            items.forEach((item) => {
                const rfvd = item?.response_form_value_data
                    ?? item?.document_response_data?.[0]?.response_form_value_data
                    ?? null
                if (rfvd) {
                    result.push({ response_form_value_data: rfvd })
                }
            })
        })
        return result
    }
    useEffect(() => {
        if (viewMode === "render") return
        if (open) {
            getDocumentData()
            if (headerReadOnly) {
                documentController.getData({}).then((response) => {
                    const dataFirst = response?.data?.find(_ => true)
                    const cols = dataFirst
                        ?.document_classify_structure_type_data?.find(_ => true)
                        ?.document_form_data?.find(_ => true)
                        ?.form_form_column_data || []
                    setHeaderDocumentColumns(cols)
                })
            }
            if (hasBody) {
                if (bodyRecord?.length > 0) {
                    setBodyData(bodyRecord)
                    if (bodyReadOnly) {
                        bodyDocumentController.getData({}).then((response) => {
                            const dataFirst = response?.data?.find(_ => true)
                            const cols = dataFirst
                                ?.document_classify_structure_type_data?.find(_ => true)
                                ?.document_form_data?.find(_ => true)
                                ?.form_form_column_data || []
                            setBodyDocumentColumns(cols)
                        })
                    }
                    // hiddenBody=true 時 body 仍需送出，只取 dbCoverId，不覆蓋預填 bodyData
                    if (hiddenBody) {
                        getBodyDocumentData({ skipSetBodyData: true })
                    }
                } else {
                    setTimeout(() => {
                        getBodyDocumentData()
                    }, bodyData?.length == 0 ? 1000 : 0)
                }
                deleteBodyDataRef.current = []
            }
            if (hasExtraBody) {
                if (extraBodyRecord?.length > 0) {
                    setExtraBodyData(extraBodyRecord)
                }
                getExtraBodyDocumentData()
                deleteExtraBodyDataRef.current = []
            }
        } else {
            bodyHeadResponseRef.current = []
            deleteBodyDataRef.current = []
            deleteExtraBodyDataRef.current = []
            submitExtraBodyRef.current = []
            setPostSetting({})
            setPostBodySetting({})
            setExtraPostBodySetting({})
            setDataLoading(false)
            setLoading(false)
            setData([])
            setBodyData([])
            setExtraBodyData([])
            setBodyDocumentColumns([])
            setHeaderDocumentColumns([])
        }
    }, [open])

    useEffect(() => {
        setPostSetting({
            dbCoverId: record?.document_id,
        })
    }, [record])

    useEffect(() => {
        if (viewMode !== "render") return
        getDocumentData()
        if (hasBody) {
            if (bodyRecord?.length > 0) {
                setBodyData(bodyRecord)
            } else {
                setTimeout(() => {
                    getBodyDocumentData()
                }, 1000)
            }
            deleteBodyDataRef.current = []
        }
    }, [])

    const BlockRender = viewMode === "modal" ? Modal : viewMode === "card" ? Card : Render

    // 組合給 ReceiptContent 用的 bodyResponseValues
    const bodyResponseValues = bodyData.map(b => b?.responseValue).filter(Boolean)

    // ── sections 設定（每次 render 更新，callback 透過 sectionsRef 讀取）──────
    const sections = [
        hasBody && {
            id: 'main',
            hidden: hiddenBody,
            data: bodyData, setData: setBodyData,
            postSetting: postBodySetting,
            submitRef: submitBodyRef, deleteRef: deleteBodyDataRef,
            headCode: bodyHeadCode,
            title: bodyTitle, createTitle: bodyCreateTitle, deleteTitle: bodyDeleteTitle,
            itemHeight: bodyHeight?.body,
            readOnly: bodyReadOnly,
            docColumns: bodyDocumentColumns,
            colLinks: bodyColumnLinks, colOnClick: bodyColumnOnClick, visibleFields: bodyVisibleFields,
            hideCreate: hiddenCreateButton || bodyReadOnly,
            preloadedDoc: preloadedBodyCanvasDocument,
            canvasMode: propsBodyCanvasMode, autoFitWidth: propsBodyAutoFitWidth,
            accentColor: '#52c41a', accentBg: '#f6ffed',
        },
        hasExtraBody && {
            id: 'extra',
            hidden: false,
            data: extraBodyData, setData: setExtraBodyData,
            postSetting: extraPostBodySetting,
            submitRef: submitExtraBodyRef, deleteRef: deleteExtraBodyDataRef,
            headCode: extraBodyHeadCode,
            title: extraBodyTitle, createTitle: extraBodyCreateTitle, deleteTitle: extraBodyDeleteTitle,
            itemHeight: bodyHeight?.extraBody,
            readOnly: false,
            docColumns: [], colLinks: {}, colOnClick: {}, visibleFields: [],
            hideCreate: false,
            preloadedDoc: null, canvasMode: undefined, autoFitWidth: undefined,
            accentColor: '#fa8c16', accentBg: '#fff7e6',
            fetchDoc: getExtraBodyDocumentData,
        },
    ].filter(Boolean)
    sectionsRef.current = sections

    // ── 統一 section 渲染（hidden/visible 共用相同邏輯）─────────────────────
    const renderSection = (section, sectionIdx) => {
        const { id, hidden, data, setData, postSetting, submitRef,
            title, createTitle, deleteTitle, itemHeight, readOnly,
            docColumns, colLinks, colOnClick, visibleFields, hideCreate,
            preloadedDoc, canvasMode, autoFitWidth, accentColor, accentBg } = section

        const processData = makeProcessSectionData(section.headCode)

        const handleDeleteItem = (idx) => {
            const item = data.find((_, i) => i === idx)
            setData(pre => pre.filter((_, i) => i !== idx))
            if (item?.responseValue?.response_id) {
                section.deleteRef.current = [...(section.deleteRef.current || []), item]
            }
        }

        const renderItemContent = (item, idx) => {
            if (readOnly) {
                return <ReadOnlyDocumentItem responseValue={item?.responseValue} columnLinks={colLinks} columnOnClick={colOnClick} documentColumns={docColumns} visibleFields={visibleFields} />
            }
            if (axiosMode === "get" || formComponent === "FormStatic5") {
                return <FormStatic5 defaultMode="fill_disable" key={postSetting?.dbCoverId} dbCoverId={item?.document_id} style={{ height: '100%' }} showSubmit={false} showToolbar={false} defaultRecord={responsesToKeyValueTableData([item?.responseValue] || [])?.find(_ => true)} {...item} {...postSetting} />
            }
            return (
                <BaseDocumentResponseCanvasEdit
                    key={hidden ? (postSetting?.dbCoverId ?? idx) : (viewMode === "render" ? `${postSetting?.dbCoverId ?? id}_${idx}` : postSetting?.dbCoverId)}
                    axiosMode={item?.responseValue?.response_id ? "patch" : "post"}
                    ref={el => (submitRef.current[idx] = el)}
                    dbCoverId={item?.document_id}
                    style={{ height: '100%' }}
                    reponseApiRoute={data.length - 1 === idx ? reponseApiRoute : responseWithoutViewApiRoute}
                    {...item}
                    {...postSetting}
                    onFinish={(args) => { if (!propsLoadRecord) handleSectionItemFinish(sectionIdx, idx, args) }}
                    processResponseData={processData}
                    preloadedDocument={preloadedDoc}
                    propsCanvasMode={canvasMode}
                    autoFitWidth={autoFitWidth}
                />
            )
        }

        if (hidden) {
            return (
                <div key={id} style={{ display: 'none' }}>
                    {data.map((item, idx) => renderItemContent(item, idx))}
                </div>
            )
        }

        return (
            <React.Fragment key={id}>
                <Col span={24}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '2px' }}>
                        {title && (
                            <div style={{ flex: 1, padding: '7px 12px', backgroundColor: accentBg, borderLeft: `4px solid ${accentColor}`, borderRadius: '4px' }}>
                                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: accentColor, letterSpacing: '0.5px' }}>{title}</h3>
                            </div>
                        )}
                        {axiosMode !== "get" && viewMode !== "card" && !hideCreate && (
                            <Button onClick={() => {
                                if (!postSetting?.dbCoverId) section.fetchDoc?.()
                                setData([...data, {}])
                            }}>{createTitle}</Button>
                        )}
                    </div>
                </Col>
                {data.map((item, idx) => (
                    <Col span={24} key={idx} style={{ display: 'flex', overflow: 'visible', marginBottom: idx < data.length - 1 ? '4px' : '0', gap: '8px', position: 'relative' }}>
                        <div
                            style={{ flex: 1, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 8, background: '#ffffff', position: 'relative' }}
                            onTouchStart={(e) => { const t = e.touches[0]; e.currentTarget.dataset.startX = t.clientX; e.currentTarget.dataset.startY = t.clientY; }}
                            onTouchMove={(e) => {
                                const t = e.touches[0]
                                const dx = t.clientX - parseFloat(e.currentTarget.dataset.startX || 0)
                                const dy = t.clientY - parseFloat(e.currentTarget.dataset.startY || 0)
                                if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
                                    const btn = e.currentTarget.parentElement.querySelector('.delete-btn-mobile')
                                    if (btn) btn.style.display = dx < -50 ? 'flex' : dx > 10 ? 'none' : btn.style.display
                                }
                            }}
                        >
                            {renderItemContent(item, idx)}
                        </div>
                        {axiosMode !== "get" && viewMode !== "card" && !readOnly && (
                            <>
                                <Button onClick={() => handleDeleteItem(idx)} type="danger" className="hidden md:flex" style={{ alignSelf: 'center', height: 'auto', minHeight: '32px', padding: '8px 16px', fontWeight: '500' }}>
                                    {deleteTitle}
                                </Button>
                                <div className="delete-btn-mobile md:hidden" style={{ display: 'none', position: 'absolute', right: 0, top: 0, height: '100%', alignItems: 'center', padding: '0 8px', backgroundColor: '#ff4d4f', borderRadius: '0 4px 4px 0', zIndex: 10 }}>
                                    <Button onClick={() => handleDeleteItem(idx)} type="danger" style={{ height: 'auto', padding: '8px 12px', fontWeight: '500' }}>{deleteTitle}</Button>
                                </div>
                            </>
                        )}
                    </Col>
                ))}
            </React.Fragment>
        )
    }

    return <>
        {/* ─── 隱藏的收據排版，供 CPCL 截圖用 ──────────────────────────────── */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', pointerEvents: 'none' }}>
            <ReceiptContent
                ref={receiptRef}
                headerResponseValue={receiptHeaderOverride ?? record?.responseValue}
                bodyResponseValues={liveBodyResponseValues}
                signatureImageUrl={signatureImageUrl}
            />
        </div>

        {/* ─── CPCL 列印預覽 Modal ─────────────────────────────────────────── */}
        <PrintPreviewModal
            previewUrl={cpclPreviewUrl}
            onClose={() => { setCpclPreviewUrl(null); cpclImageDataRef.current = null; flushPendingFinish(); }}
            onConfirm={handleCpclConfirmPrint}
            loading={cpclPrintLoading}
            deviceName={window.selected_device?.name}
        />

        <BlockRender
            confirmLoading={loading}
            title={<>
                {title}
                {propsPrint && <Button size="small" onClick={handlePrint} style={{ marginLeft: '16px' }}>{window.selected_device ? "🖨️ CPCL 列印" : "🖨️ 預覽列印"}</Button>}
            </>}
            open={open}
            onOk={axiosMode !== "get" ? handleOk : null}
            onClose={() => {
                if (printPending) return
                setOpen(false)
            }}
            okText={okText}
            close={true}
            okProps={{}}
            cancelText="取消"
            closeText="關閉"
            width={width}
            axiosMode={axiosMode}
            setOpen={(v) => { if (!v && printPending) return; setOpen(v) }}
            loading={loading}
            handleOk={handleOk}
            printPending={printPending}
        >
            <Row gutter={[4, 2]} ref={labelRef}>
                {viewMode === "card" && <Button onClick={handleOk}>{okText}</Button>}
                {showDocumentButton && axiosMode === "post" && !!postSetting?.dbCoverId && <Col span={24}>
                    <Row gutter={[8, 8]} style={{ alignItems: 'center' }}   >
                        {
                            documentController?.ResponseData.map((ele) => {
                                return (<Col>
                                    <Button
                                        key={ele.document_id}
                                        type={parseInt(postSetting?.dbCoverId) === parseInt(ele?.document_id) ? null : "default"}
                                        onClick={() => {
                                            setPostSetting({
                                                dbCoverId: ele?.document_id,
                                                document_name: ele?.document_name,
                                            })
                                        }}>{ele?.document_name}
                                    </Button>
                                </Col>)
                            })
                        }
                    </Row>
                </Col>}

                <Col span={24}>
                    {headerTitle !== "單頭標題" &&
                        <div style={{
                            padding: '7px 12px',
                            backgroundColor: '#fff7e6',
                            borderLeft: '4px solid #fa8c16',
                            borderRadius: '4px',
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#d46b08',
                                letterSpacing: '0.5px'
                            }}>
                                {headerTitle}
                            </h3>
                        </div>
                    }
                </Col>
                <Col span={24} style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    background: '#ffffff',
                    overflow: 'visible',
                }}>
                    {headerReadOnly ?
                        <ReadOnlyDocumentItem
                            responseValue={record?.responseValue}
                            columnLinks={headerColumnLinks}
                            columnOnClick={headerColumnOnClick}
                            documentColumns={headerDocumentColumns}
                            visibleFields={headerVisibleFields}
                        /> :
                        axiosMode === "get" || formComponent === "FormStatic5" ?
                            <FormStatic5
                                defaultMode={"fill_disable"}
                                key={postSetting?.dbCoverId}
                                dbCoverId={record?.document_id}
                                style={{ height: '100%' }}
                                showSubmit={false}
                                showToolbar={false}
                                defaultRecord={responsesToKeyValueTableData([record?.responseValue] || [])?.find((_) => true)}
                                {...record}
                                {...postSetting}
                            /> :
                            <BaseDocumentResponseCanvasEdit
                                key={postSetting?.dbCoverId}
                                axiosMode={axiosMode}
                                ref={submitRef}
                                dbCoverId={record?.document_id}
                                style={{ height: '100%' }}
                                reponseApiRoute={
                                    hasBody ? responseWithoutViewApiRoute : reponseApiRoute
                                }
                                {...record}
                                {...(propsLoadRecord ? { ...(data?.[0] ?? {}) } : {})}
                                {...postSetting}
                                onFinish={({ axiosMode, status, values, payload }) => {
                                    setLoading(false)
                                    if (hasBody || hasExtraBody) {
                                        if (status !== "success") return
                                        headerResponseRef.current = values
                                        headerPayloadRef.current = payload
                                        bodyHeadResponseRef.current = { ...values }
                                        headerResponseRef.current = Array.isArray(values?.payload)
                                            ? values.payload.find(_ => true)
                                            : values?.payload ?? payload ?? values
                                        // 找第一個有 items 的 section 提交，全空則直接 finish
                                        const activeSections = sectionsRef.current
                                        const firstIdx = activeSections.findIndex(s => s.submitRef.current.length > 0)
                                        if (firstIdx !== -1) {
                                            submitSection(firstIdx)
                                        } else {
                                            emitOnFinish({ axiosMode, status, values: headerResponseRef.current, headerValues: headerResponseRef.current })
                                        }
                                    } else {
                                        emitOnFinish({ axiosMode, status, values })
                                    }
                                }}
                                processResponseData={processHeaderResponseData}
                                defaultRecord={defaultRecord}
                                preloadedDocument={preloadedHeaderCanvasDocument}
                                propsCanvasMode={propsCanvasMode}
                                editableFieldCodes={headerPartialEdit ? headerEditableFieldCodes : null}
                            />

                    }
                </Col>
                {sections.map((section, sectionIdx) => renderSection(section, sectionIdx))}
            </Row>

        </BlockRender>
    </>
})
BaseDocumentResponseCanvasEditModal.displayName = 'BaseDocumentResponseCanvasEditModal'
export default BaseDocumentResponseCanvasEditModal